import { convertHexToHsl } from "@app/common/utils/colorUtils";
import { IPropertyTheme } from "@app/property/types";
import { UtilsNumeric } from "@hotelchamp/common";

import { ITheme } from "../components/ThemeProvider";
import { CssMeasurementUnits } from "../constants/CssMeasurementUnits";
import { configManager } from "../services/configuration/ConfigManager";

const IBE_FALLBACK_COLOR = "#0085FF";

const IBE_GLOBAL_COLOR = configManager.config.ibe.primaryColor;

/**
 * constructUnifiedThemeConfig
 * Since we have theme config from multiple sources in multiple formats, this function
 * will construct a unified theme config object that will be used in the app
 */
export const constructUnifiedThemeConfig = (propertyTheme?: IPropertyTheme, themeConfig?: ITheme): ITheme => {
    const fallbackColor = propertyTheme?.colors.primary || IBE_GLOBAL_COLOR || IBE_FALLBACK_COLOR;
    const nextThemeConfig = {
        ...(themeConfig || {}),
        key: themeConfig?.key || "light",
    };

    if (propertyTheme) {
        if (propertyTheme?.border_radius) {
            nextThemeConfig.borderRadius = getCustomBorderRadiusFromTheme(propertyTheme);
        }

        if (propertyTheme?.colors) {
            nextThemeConfig.colors = {
                primary: propertyTheme?.colors.primary || fallbackColor,
            };
        }

        if (propertyTheme?.label_widget_offset) {
            nextThemeConfig.labelWidgetOffset = getCustomLabelWidgetOffsetFromTheme(propertyTheme.label_widget_offset);
        }
    }

    return nextThemeConfig;
};

export interface IApplyThemeToHtmlElementProps {
    targetElement: HTMLElement;
    theme: ITheme;
}

export const applyThemeToHtmlElement = ({ targetElement, theme }: IApplyThemeToHtmlElementProps): boolean => {
    const primaryColor = theme?.colors?.primary;
    const hslColor = primaryColor ? convertHexToHsl(primaryColor) : null;
    const borderRadius = theme?.borderRadius;

    if (!targetElement) {
        return false;
    }

    let hasBeenApplied = true;

    if (hslColor !== null) {
        const colorString = `${hslColor.h}, ${hslColor.s}, ${hslColor.l}`;
        const colorArray = colorString.split(",");

        colorArray[2] = " 92%";

        targetElement.style.setProperty("--hc-primary", colorString);
        targetElement.style.setProperty("--hc-secondary", colorArray.join(","));
        targetElement.style.setProperty("--hc-secondary-foreground", colorString);

        hasBeenApplied = hasBeenApplied && true;
    }

    if (borderRadius) {
        targetElement.style.setProperty("--hc-border-radius-none", borderRadius.none);
        targetElement.style.setProperty("--hc-border-radius-sm", borderRadius.sm);
        targetElement.style.setProperty("--hc-border-radius-default", borderRadius.default);
        targetElement.style.setProperty("--hc-border-radius-md", borderRadius.md);
        targetElement.style.setProperty("--hc-border-radius-lg", borderRadius.lg);
        targetElement.style.setProperty("--hc-border-radius-xl", borderRadius.xl);
        targetElement.style.setProperty("--hc-border-radius-2xl", borderRadius["2xl"]);
        targetElement.style.setProperty("--hc-border-radius-3xl", borderRadius["3xl"]);
        targetElement.style.setProperty("--hc-border-radius-full", borderRadius.full);

        hasBeenApplied = hasBeenApplied && true;
    }

    return hasBeenApplied;
};

export const getCustomBorderRadiusFromTheme = (propertyTheme: IPropertyTheme): IPropertyTheme["border_radius"] => ({
    none: "0px",
    sm: "0.125rem",
    default: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
    ...(propertyTheme?.border_radius || {}),
});

/**
 * "default" is the none prefixed, so mobile, version
 * @param offset
 * @returns
 */
export const getCustomLabelWidgetOffsetFromTheme = (offset: IPropertyTheme["label_widget_offset"]) => ({
    x: {
        default: "8px",
        sm: "8px",
        md: "16px",
        lg: "16px",
        xl: "16px",
        "2xl": "16px",
        ...(offset?.x || {}),
    },
    y: {
        default: "8px",
        sm: "8px",
        md: "12px",
        lg: "12px",
        xl: "12px",
        "2xl": "12px",
        ...(offset?.y || {}),
    },
});

/**
 * Simple helper to extract the numeric value from a string which contains
 * the numeric value including the unit (e.g. 20px, 3em, 50%, etc)
 *
 * @param value
 */
const extractNumericValue = (value: string | number): number | null => {
    const extracted = parseFloat(String(value));

    return UtilsNumeric.isNumeric(extracted) ? extracted : null;
};

/**
 * Extract the unit from a value (widget dimension width of optionally height).
 * If no unit is provided, force Units.Pixels
 *
 * @param value
 */
const extractUnit = (value: string | number): CssMeasurementUnits | null => {
    const str = String(value);
    let unit = null;

    if (str.indexOf("%") !== -1) {
        unit = CssMeasurementUnits.Percentage;
    } else if (str.indexOf("rem") !== -1) {
        unit = CssMeasurementUnits.RootEm;
    } else if (str.indexOf("em")) {
        unit = CssMeasurementUnits.Em;
    } else if (str.indexOf("px") || !isNaN(Number(value))) {
        unit = CssMeasurementUnits.Pixels;
    }

    return unit;
};
